import { OccasionDate, Reminder, Repeat } from './occasion-repeat'
import { eventOccasion } from './occasion-event'
import { occasionRange } from './occasion-range'

// The shape of an Emrouz event and the rule that decides when it comes back.
// Pure: no storage, no plugin, no DOM, so the rule can be read — and tested —
// on its own. The store that holds these lives in services/events.ts.
//
// The events collection holds both appointments and occasions. Appointments
// use Gregorian clock recurrence; occasions retain their original calendar
// date and publish calculated all-day occurrences to the phone.

export type EventFreq = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'

export interface IEventRepeat {
    freq: EventFreq
    interval: number // every N periods; 1 unless the user asks for more
    until?: number // epoch ms; the last day the series may land on
    calendar?: Exclude<Repeat, 'none'>
}

// One copy of an event that an older version of Emrouz wrote into a phone
// calendar. Emrouz writes none now, and the ones already there were left as the
// user's own. The id is the phone's, which keeps the copy out of what Emrouz
// reads back — otherwise the same event would be drawn twice on its day.
export interface IPublishedCopy {
    calendar: string // the phone calendar's name
    id: string // the phone's own event id
}

export interface IEvent {
    id: string
    title: string
    allDay: boolean
    start: number // epoch milliseconds
    end: number // epoch milliseconds
    location?: string
    notes?: string
    repeat: IEventRepeat
    reminder: Reminder
    kind?: 'occasion'
    occasionDate?: OccasionDate
    source?: string
    legacyOccasionId?: string
    // The copies an older version wrote into the phone's calendars, kept only
    // so they are not read back. See IPublishedCopy.
    published?: IPublishedCopy[]
    // Insertion order. IndexedDB returns key order and shortUUID ids are
    // random, so add() stamps this and init() sorts by it. Optional so rows
    // saved before it existed keep the engine's load order.
    seq?: number
}

// One time the event happens.
export interface IOccurrence {
    start: number
    end: number
}

const DAY_MS = 24 * 60 * 60 * 1000

// A guard, not a horizon. The walk below starts at the first occurrence the
// asked-for range can contain, so this only ever stops a daily event asked for
// over an absurd span.
const MAX_STEPS = 800

export const noRepeat = (): IEventRepeat => ({ freq: 'none', interval: 1 })

// A day past the target month's length falls on that month's last day — the
// same rule an occasion anniversary follows, so a monthly event made on the
// 31st never skips a short month in silence.
function clamped(y: number, m: number, d: number, h: number, min: number): Date {
    // Through day 1 first, so an out-of-range month index normalises before the
    // length is measured.
    const month = new Date(y, m, 1)
    const length = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    return new Date(month.getFullYear(), month.getMonth(), Math.min(d, length), h, min)
}

// The nth occurrence's start, counted from the first.
//
// Built from date parts rather than by adding milliseconds, so a daylight-saving
// change inside the series does not drag the clock time with it: a 9am weekly
// meeting stays at 9am across the change, which is what every calendar means by
// weekly.
function advance(first: Date, freq: EventFreq, n: number): Date {
    const y = first.getFullYear()
    const m = first.getMonth()
    const d = first.getDate()
    const h = first.getHours()
    const min = first.getMinutes()
    switch (freq) {
        case 'daily': return new Date(y, m, d + n, h, min)
        case 'weekly': return new Date(y, m, d + n * 7, h, min)
        case 'monthly': return clamped(y, m + n, d, h, min)
        case 'yearly': return clamped(y + n, m, d, h, min)
        default: return new Date(first)
    }
}

// Roughly how many steps in the range begins, so a window years away from the
// first occurrence is not walked to one day at a time. Deliberately an
// underestimate — the walk itself decides what is really in range, and a couple
// of steps of slack absorbs the clamping and the daylight-saving shifts that
// make this arithmetic approximate.
function firstStep(first: Date, freq: EventFreq, interval: number, from: number): number {
    if (from <= first.getTime()) return 0
    const gap = from - first.getTime()
    let n = 0
    if (freq === 'daily') {
        n = Math.floor(gap / DAY_MS / interval)
    } else if (freq === 'weekly') {
        n = Math.floor(gap / (DAY_MS * 7) / interval)
    } else {
        const to = new Date(from)
        const months = (to.getFullYear() - first.getFullYear()) * 12 + (to.getMonth() - first.getMonth())
        n = Math.floor((freq === 'yearly' ? Math.floor(months / 12) : months) / interval)
    }
    return Math.max(0, n - 2)
}

// Every time the event happens inside [from, to), as instants.
//
// Half-open at both ends the way the display is: an occurrence counts when it
// is still running at `from` and has started before `to`.
export function occurrencesInRange(e: IEvent, from: number, to: number): IOccurrence[] {
    const occasion = eventOccasion(e)
    if (occasion) return occasionRange(occasion, from, to)
    const duration = Math.max(0, e.end - e.start)
    const out: IOccurrence[] = []

    if (e.repeat.freq === 'none') {
        if (e.start < to && e.start + duration > from) out.push({ start: e.start, end: e.start + duration })
        return out
    }

    const first = new Date(e.start)
    const interval = Math.max(1, Math.round(e.repeat.interval || 1))
    // `until` names a day, not an instant, so a last occurrence on that day
    // still counts.
    const until = e.repeat.until === undefined
        ? Infinity
        : new Date(new Date(e.repeat.until).setHours(23, 59, 59, 999)).getTime()

    const start = firstStep(first, e.repeat.freq, interval, from)
    for (let i = 0; i < MAX_STEPS; i++) {
        const at = advance(first, e.repeat.freq, (start + i) * interval).getTime()
        if (at >= to || at > until) break
        if (at + duration > from) out.push({ start: at, end: at + duration })
    }
    return out
}
